import {detectLocale,localeCodes,localeNames,localePath} from './locale-copy.js';
export const language=typeof location==='undefined'?'en':detectLocale(location.pathname);
// English is already in the components. Load only the current locale.
const dictionaries = typeof location==='undefined' ? await Promise.all([
 import('./translations-transaction.js?v=token-fix-20261009'),
 import('./translations-faq.js'), import('./translations-privacy.js'),
 import('./translations.js'), import('./translations-extra.js'), import('./translations-content.js')
]) : language==='en' ? [] : [await import('./translations-'+language+'-20261013.js')];
export const translations=Object.assign({},...dictionaries.map(module=>Object.values(module)[0]));
export const basename=language==='en'?'':'/'+language;
export function translate(value,lang=language){
 if(typeof value!=='string'||lang==='en')return value;
 const key=value.trim().replace(/\s+/g,' ');
 const result=translations[key]?.[lang] || translatePattern(key,lang);
 if(result)return (value.match(/^\s*/)?.[0]||'')+result+(value.match(/\s*$/)?.[0]||'');
 return value;
}
export function publicLocalePath(path,lang=language){
 if(!path?.startsWith('/')||path.startsWith('//'))return path;
 if(/^\/(?:swap(?:[/?#]|$)|contact-us(?:[/?#]|$)|privacy-policy(?:[/?#]|$)|tos(?:[/?#]|$)|(?:[?#]|$))/.test(path))return localePath(path,lang);
 if(path.startsWith('/#'))return localePath(path,lang);
 return path;
}
export function installTranslations(jsx){
 if(language==='en')return jsx;
 for(const key of ['jsx','jsxs']){
  const original=jsx[key];
  jsx[key]=(type,props,...rest)=>{
   if(props){
    props={...props};
    const tr=v=>Array.isArray(v)?v.map(tr):typeof v==='string'?translate(v):v;
    props.children=tr(props.children);
    for(const attr of ['placeholder','title','aria-label'])if(props[attr])props[attr]=translate(props[attr]);
    if(type==='a'&&props.href)props.href=publicLocalePath(props.href);
   }
   return original(type,props,...rest);
  };
 }
 if(typeof document!=='undefined')document.documentElement.lang=language;
 return jsx;
}
export function localizedReact(React){
 if(language==='en')return React;
 return {...React,createElement:(type,props,...children)=>{
  if(true){
   props={...props};for(const key of ['placeholder','title','aria-label'])if(props[key])props[key]=translate(props[key]);
   if(type==='a'&&props.href)props.href=publicLocalePath(props.href);
   const tr=v=>Array.isArray(v)?v.map(tr):typeof v==='string'?translate(v):v;
   children=children.map(tr);
  }
  return React.createElement(type,props,...children);
 }};
}
export function createLanguageSelector(React,useLocation){
 const h=React.createElement;
 return function LanguageSelector(){
  useLocation();const ref=React.useRef(null);
  React.useEffect(()=>{const close=e=>{if(ref.current&&!ref.current.contains(e.target))ref.current.open=false};const escape=e=>{if(e.key==='Escape'&&ref.current){ref.current.open=false;ref.current.querySelector('summary')?.focus()}};document.addEventListener('pointerdown',close);document.addEventListener('keydown',escape);return()=>{document.removeEventListener('pointerdown',close);document.removeEventListener('keydown',escape)}},[]);
  const raw=location.pathname.replace(/^\/(de|es|fr|pl|uk|ru)(?=\/|$)/,'')||'/';
  // Only link to translations that exist; other pages return to the selected homepage.
  const target=raw==='/'||raw==='/swap'||raw.startsWith('/swap/')||raw.startsWith('/exchange/')||['/contact-us','/privacy-policy','/tos','/tos/'].includes(raw)?raw:'/';
  return h('details',{className:'language-picker',ref},h('summary',{'aria-label':language.toUpperCase()+' — '+localeNames[language]},h('span',{className:'language-flag flag-'+language,'aria-hidden':true}),h('span',null,language.toUpperCase()),h('span',{'aria-hidden':true},'⌄')),h('nav',{'aria-label':'Languages'},localeCodes.map(code=>h('a',{key:code,href:localePath(target,code)+location.search+location.hash,lang:code,hrefLang:code,'aria-current':code===language?'true':undefined},h('span',{className:'language-flag flag-'+code,'aria-hidden':true}),localeNames[code]))));
 };
}

const patternRows={
 swap:['{a} tauschen','Cambiar a {a}','Échanger contre {a}','Wymień na {a}','Обміняти на {a}','Обменять на {a}'],
 how:['So tauschen Sie {a} in {b}','Cómo cambiar {a} por {b}','Comment échanger {a} contre {b}','Jak wymienić {a} na {b}','Як обміняти {a} на {b}','Как обменять {a} на {b}'],
 reverse:['Umgekehrtes Paar: {a} in {b} →','Par inverso: {a} a {b} →','Paire inverse : {a} vers {b} →','Odwrotna para: {a} na {b} →','Зворотна пара: {a} на {b} →','Обратная пара: {a} на {b} →'],
 networks:['{a}-Netzwerke','Redes de {a}','Réseaux {a}','Sieci {a}','Мережі {a}','Сети {a}'],
 recipient:['{a}-Empfängeradresse','Dirección de destino de {a}','Adresse de réception {a}','Adres odbiorcy {a}','Адреса одержувача {a}','Адрес получателя {a}'],
 refund:['{a}-Rückerstattungsadresse eingeben (optional)','Dirección de reembolso de {a} (opcional)','Adresse de remboursement {a} (facultatif)','Adres zwrotu {a} (opcjonalnie)','Адреса повернення {a} (необов’язково)','Адрес возврата {a} (необязательно)']
};
function translatePattern(key,lang){
 let m,kind;
 if(m=key.match(/^Network: (.+)$/))return translate('Network:',lang)+' '+m[1];
 if(m=key.match(/^Swap to (.+) →$/))kind='swap';
 else if(m=key.match(/^How to swap (.+) to (.+)$/))kind='how';
 else if(m=key.match(/^Reverse pair: (.+) to (.+) →$/))kind='reverse';
 else if(m=key.match(/^(.+) networks$/))kind='networks';
 else if(m=key.match(/^Recipient (.+) Address$/))kind='recipient';
 else if(m=key.match(/^Enter (.+) refund address \(optional\)$/))kind='refund';
 if(!kind)return '';
 return patternRows[kind][localeCodes.indexOf(lang)-1]?.replace('{a}',m[1]).replace('{b}',m[2]||'');
}
export function updateAlternates(path,enabled=true){
 document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(n=>n.remove());
 if(!enabled)return;
 for(const code of [...localeCodes,'x-default']){const el=document.createElement('link');el.rel='alternate';el.hreflang=code;el.href='https://www.ghostswap.app'+localePath(path,code==='x-default'?'en':code);document.head.appendChild(el);}
}
